/**
 * ProfileBioSectionBeacons Component
 * 
 * Beacons-style bio section with view/edit toggle
 */

import React, { useState, useEffect } from 'react';
import type { InfluencerProfile } from '../../lib/models/influencer';
import ProfileSectionShell from './ProfileSectionShell';
import SectionEditModal from './SectionEditModal';
import EditIconButton from './EditIconButton';

interface ProfileBioSectionBeaconsProps {
  profile: InfluencerProfile;
  isOwner?: boolean;
  onBioChange?: (bio: string) => void;
}

const ProfileBioSectionBeacons: React.FC<ProfileBioSectionBeaconsProps> = ({
  profile,
  isOwner = false,
  onBioChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editBio, setEditBio] = useState(profile.bio || '');
  const [isSaving, setIsSaving] = useState(false);

  const displayBio = profile.bio || '';

  // Reset form when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setEditBio(profile.bio || '');
    }
  }, [isModalOpen, profile.bio]);

  const handleSave = async () => {
    if (onBioChange) {
      setIsSaving(true);
      try {
        await onBioChange(editBio);
        setIsModalOpen(false);
      } catch (err) {
        console.error('Error saving bio:', err);
      } finally {
        setIsSaving(false);
      }
    } else {
      setIsModalOpen(false);
    }
  };

  const handleCancel = () => {
    setEditBio(displayBio);
    setIsModalOpen(false);
  };

  if (!displayBio && !isOwner) {
    return null;
  }

  return (
    <>
      <ProfileSectionShell
        title="About Me"
        actionLabel={isOwner ? 'Edit' : undefined}
        onActionClick={isOwner ? () => setIsModalOpen(true) : undefined}
      >
        <div>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {displayBio || (isOwner ? 'No bio yet. Click Edit to add one.' : 'No bio available.')}
          </p>
        </div>
      </ProfileSectionShell>

      {isOwner && (
        <SectionEditModal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title="Edit About Me"
          subtitle="Share a quick intro brands can understand in seconds."
          onSave={handleSave}
          isSaving={isSaving}
          errors={{}}
        >
          <div className="space-y-4">
            <textarea
              value={editBio}
              onChange={(e) => setEditBio(e.target.value)}
              rows={6}
              maxLength={600}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent resize-y"
              placeholder="Tell brands about yourself, your content style, and what makes you unique..."
            />
            <p className="text-xs text-slate-500">
              Max 600 characters ({editBio.length} / 600)
            </p>
          </div>
        </SectionEditModal>
      )}
    </>
  );
};

export default ProfileBioSectionBeacons;

